"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCheckOutline18, IconChevronExpandYOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
function stripDexPrefix(coin: string): string {
  const idx = coin.indexOf(":");
  return idx !== -1 ? coin.slice(idx + 1) : coin;
}
function CoinLogo(t0) {
  const $ = _c(7);
  const {
    coin,
    size
  } = t0;
  let t1;
  if ($[0] !== coin) {
    t1 = stripDexPrefix(coin).toLowerCase();
    $[0] = coin;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  const t2 = `https://ai-icon.b3.fun/logo/${name}`;
  const t3 = `${name} logo`;
  let t4;
  if ($[2] !== name || $[3] !== size || $[4] !== t2 || $[5] !== t3) {
    t4 = <img key={name} src={t2} alt={t3} width={size} height={size} className="rounded-full" loading="lazy" onError={_temp} />;
    $[2] = name;
    $[3] = size;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp(e) {
  (e.target as HTMLImageElement).style.visibility = "hidden";
}
interface HyperliquidMarket {
  coin: string;
  szDecimals: number;
  maxLeverage: number;
  onlyIsolated: boolean;
}
interface HyperliquidCoinSelectorProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string | undefined;
  onChange: (value: string) => void;
  required?: boolean;
  popoverModal?: boolean;
  dex?: string;
}
async function fetchHyperliquidMarkets(signal: AbortSignal, dex?: string): Promise<HyperliquidMarket[]> {
  const payload: Record<string, unknown> = {};
  if (dex) payload.dex = dex;
  const response = await authenticatedFetch<{
    code: number;
    data: {
      result: {
        markets: HyperliquidMarket[];
      };
      success: boolean;
    };
  }>(`${V2_ENDPOINT}/v1/action-proxy/hyperliquid-get-markets/query`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      payload
    }),
    signal
  });
  if (!response.ok) return [];
  const data = await response.json();
  if (data.code !== 0 || !data.data?.success) return [];
  return data.data.result.markets ?? [];
}
function HyperliquidCoinSelector({
  label,
  description,
  headerRight,
  value,
  onChange,
  required = false,
  popoverModal = false,
  dex
}: HyperliquidCoinSelectorProps) {
  const [containerEl, setContainerEl] = React.useState<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const {
    data: markets = [],
    isLoading
  } = useQuery({
    queryKey: ["hyperliquid-markets", dex ?? "default"],
    queryFn: ({
      signal
    }) => fetchHyperliquidMarkets(signal, dex),
    staleTime: 5 * 60 * 1000
  });
  const prevDexRef = React.useRef(dex);
  React.useEffect(() => {
    if (prevDexRef.current !== dex) {
      prevDexRef.current = dex;
      if (value) onChange("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref guard handles value; only dex change matters
  }, [dex, onChange]);
  const filteredMarkets = React.useMemo(() => {
    if (!searchQuery) return markets;
    const query = searchQuery.toLowerCase();
    return markets.filter(m => stripDexPrefix(m.coin).toLowerCase().includes(query));
  }, [markets, searchQuery]);
  const selectedMarket = React.useMemo(() => markets.find(m => m.coin === value) ?? null, [markets, value]);
  return <div ref={setContainerEl} className="space-y-2">
      {label && <div className="flex items-center justify-between gap-3">
          <label className="field-label flex items-center gap-2">
            <span>{label}</span>
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
          {headerRight}
        </div>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      <Popover open={open} onOpenChange={nextOpen => {
      setOpen(nextOpen);
      if (!nextOpen) setSearchQuery("");
    }} modal={popoverModal}>
        <PopoverTrigger asChild>
          <button type="button" role="combobox" aria-expanded={open} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
            {selectedMarket ? <span className="flex items-center gap-2">
                <CoinLogo coin={selectedMarket.coin} size={20} />
                <span className="font-medium">{stripDexPrefix(selectedMarket.coin)}</span>
                <span className="text-ds-text-muted text-xs">{selectedMarket.maxLeverage}x max</span>
              </span> : value ? <span className="flex items-center gap-2">
                <CoinLogo coin={value} size={20} />
                <span className="font-medium">{stripDexPrefix(value)}</span>
              </span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">Select coin...</span>}
            <IconChevronExpandYOutline18 className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="bg-ds-surface-card !z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" side="bottom" container={popoverModal ? containerEl ?? undefined : undefined}>
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search coins..." value={searchQuery} onValueChange={setSearchQuery} />
            <CommandList className="max-h-64">
              {isLoading ? <div className="body-sm py-6 text-center">Loading markets...</div> : <>
                  {filteredMarkets.length === 0 && <div className="text-ds-text-muted py-6 text-center text-sm">No coin found.</div>}
                  <CommandGroup>
                    {filteredMarkets.map(market => <CommandItem key={market.coin} value={market.coin} onSelect={() => {
                  onChange(value === market.coin ? "" : market.coin);
                  setOpen(false);
                }} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800">
                        <div className="flex w-full cursor-pointer items-center gap-2">
                          <IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", value === market.coin ? "opacity-100" : "opacity-0")} />
                          <CoinLogo coin={market.coin} size={20} />
                          <span className="flex-1 font-medium">{stripDexPrefix(market.coin)}</span>
                          <span className="text-ds-text-muted text-xs">{market.maxLeverage}x</span>
                        </div>
                      </CommandItem>)}
                  </CommandGroup>
                </>}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>;
}
export default React.memo(HyperliquidCoinSelector);